// =============================================================================
// PM Bulk Edit — a portfolio-wide spreadsheet editor.
//
// Two modes: PROJECTS (a row per project, columns picked from spine fields / parameters / documents)
// and PARAMETERS (a row per project x parameter, for filling one detail across the whole portfolio).
//
// Deliberately NOT reactive: pages are method-fetched snapshots, so rows never reorder or repaint under
// someone mid-edit. Freshness = Refresh + paging; staleness is caught by per-cell updatedAt tokens, and
// a conflicted cell is flagged amber instead of silently overwriting a teammate.
// =============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { Checkbox } from '/imports/ui/Checkbox';
import { DividerTabs } from '/imports/ui/DividerTabs';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { toastOnError } from '/imports/ui/utils';
import { useUser, useFindOne, useSubscribe, useTracker, useMethod } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { Accounts } from '/imports/api/accounts';
import { FileInput } from '/imports/ui/FileInput';
import { Icon } from '/imports/ui/Icon';
import {
  ProjectManagementBulkColumn, ProjectManagementBulkRow, ProjectManagementBulkEdit, ProjectManagementBulkParameterRow,
  ProjectManagementBulkPreset, ProjectManagementBulkPresets, projectManagementSaveBulkPreset,
  projectManagementDeleteBulkPreset, projectManagementBulkPresetPrune,
  ProjectManagementPortfolios, ProjectManagementOwners, ProjectManagementEpcs, ProjectManagementUtilities, ProjectManagementParty, projectManagementBulkColId, projectManagementBulkGrid, projectManagementBulkParameters,
  projectManagementBulkApply, projectManagementBulkApplyMax, projectManagementBatchUploadDocument,
  projectManagementBulkExport, projectManagementBulkImport, ProjectManagementBulkImportResult,
  projectManagementCanAccess, projectManagementIsAdmin, projectManagementLifecyclesInUse,
  ProjectManagementBulkColumnFilter, ProjectManagementBulkSort, ProjectManagementParameterType,
  projectManagementBulkColumnValues, projectManagementSpineFieldType, projectManagementProjectStatuses, projectManagementExternalIdLabel, projectStatusLabel,
  projectManagementFormatValue as formatValue,
} from '/imports/api/projectManagement';
import { Popover, useAnchorRect } from '/imports/ui/Popover';
import { ProjectManagementColumnFilter } from '/imports/ui/ProjectManagementColumnFilter';
import {
  getProjectManagementLifecycle, projectManagementLifecycles, projectManagementDefaultLifecycleKey,
  projectManagementWorkspaceLifecycle, projectManagementSetWorkspaceLifecycle,
} from '/imports/api/projectManagementLifecycles';
import { ProjectManagementBulkGrid } from '/imports/ui/ProjectManagementBulkGrid';
import { projectManagementValueInput, projectManagementToStored, projectManagementParseCell } from '/imports/ui/ProjectManagementCellEditor';
import { maxSpreadsheetUploadBytes } from '/imports/api/files';

const PAGE_SIZE = 50;
// Per vertical: the column set is built from one lifecycle's parameters and doc slots, so carrying RIC's
// into residential would show a screen of columns with nothing behind them. The unsuffixed key is what
// every RIC user's columns are saved under today, read once as the default vertical's set so this does
// not quietly reset them.
const columnsKey = (lifecycleKey: string) => `pm.bulk.columns.${lifecycleKey}`;
const LEGACY_COLUMNS_KEY = 'pm.bulk.columns';
const FILTERS_KEY = 'pm.bulk.filters';
const SEARCH_DEBOUNCE_MS = 350;
const FONT_SCALE_KEY = 'pm.bulk.fontScale';
const COL_WIDTHS_KEY = 'pm.bulk.colWidths';
// Spine columns only: every vertical has them, so this is a sane opening hand in any book.
const DEFAULT_COLUMNS: ProjectManagementBulkColumn[] = [
  { kind: 'spine', key: 'externalId' }, { kind: 'spine', key: 'ownerId' },
  { kind: 'spine', key: 'utilityId' }, { kind: 'spine', key: 'capacityKwDc' },
];

/** The columns saved for one vertical, falling back to the pre-per-vertical key for the default
 *  vertical and then to the opening set. */
const readColumns = (lifecycleKey: string): ProjectManagementBulkColumn[] => {
  const read = (key: string) => {
    try {
      const saved = JSON.parse((typeof localStorage !== 'undefined' && localStorage.getItem(key)) || 'null');
      return Array.isArray(saved) && saved.length ? saved as ProjectManagementBulkColumn[] : undefined;
    }
    catch (_) {
      return undefined;
    }
  };
  const legacy = lifecycleKey === projectManagementDefaultLifecycleKey ? read(LEGACY_COLUMNS_KEY) : undefined;
  return read(columnsKey(lifecycleKey)) || legacy || DEFAULT_COLUMNS;
};

/** Per-column pixel widths keyed by colId. Device-local like the columns and filters beside it —
 *  a saved preset carries which columns to show, deliberately not how wide one person likes them. */
const readColWidths = (): Record<string, number> => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(COL_WIDTHS_KEY) : null;
    const saved = raw ? JSON.parse(raw) : null;
    if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return {};
    return Object.fromEntries(Object.entries(saved).filter(([, v]) => typeof v === 'number' && v > 0)) as Record<string, number>;
  }
  catch (_) {
    return {};
  }
};
// The grid's type is sized in rem (root-relative), so a parent font-size can never reach it. A custom
// property can — it inherits down the tree and each rule multiplies its own base by it. Chosen over
// `transform: scale`, which would blur text, scale the scrollbars and make the container a containing
// block for any position: fixed descendant.
const FONT_SCALES = [0.8, 0.9, 1, 1.1, 1.25, 1.5];
const readFontScale = (): number => {
  const saved = Number(typeof localStorage !== 'undefined' ? localStorage.getItem(FONT_SCALE_KEY) : NaN);
  return FONT_SCALES.includes(saved) ? saved : 1;
};

/** Versioned so a future shape change gets a migration branch instead of a wedged page. */
interface BulkFilterState {
  v: 1;
  filters: ProjectManagementBulkColumnFilter[];
  sort?: ProjectManagementBulkSort;
}
const emptyFilterState = (): BulkFilterState => ({ v: 1, filters: [] });

/** Reads persisted filters, discarding anything whose shape doesn't survive a version bump or a
 *  hand-edit of localStorage. Column identity is re-checked against the live catalogue separately,
 *  because the template can rename a parameter between sessions. */
const readFilterState = (): BulkFilterState => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(FILTERS_KEY) : null;
    const saved = raw ? JSON.parse(raw) : null;
    if (!saved || saved.v !== 1 || !Array.isArray(saved.filters)) return emptyFilterState();
    // JSON has no Date type — revive the boundaries the predicate compares against.
    const revive = (f: any): ProjectManagementBulkColumnFilter => ({
      ...f,
      date: f.date ? new Date(f.date) : undefined,
      date2: f.date2 ? new Date(f.date2) : undefined,
    });
    return { v: 1, filters: saved.filters.filter((f: any) => f?.column?.kind && f?.op).map(revive), sort: saved.sort };
  }
  catch (_) {
    return emptyFilterState();
  }
};

const BulkPageStyled = styled(Page)`
  /* The page root defaults to min-width: auto as a flex item, so a 10k-px-wide grid table drags the
     whole page (and the toolbar) wider than the viewport. Pinning it to 0 makes the GRID the only thing
     that scrolls sideways. */
  min-width: 0;
  /* min-width: 0 all the way down, or the 10k-px-wide grid table stretches the whole column and the
     toolbar with it (measured: a 50-column grid made this toolbar 10297px wide in a 1918px viewport). */
  .page-body { height: 0; display: flex; flex-direction: column; min-width: 0; overflow: hidden; }
  .heading-buttons { display: flex; gap: 0.5rem; align-items: center; }
  .bulk-panel { flex: 1; min-height: 0; min-width: 0; display: flex; flex-direction: column; overflow: hidden; box-sizing: border-box; }
  /* Two deliberate rows — filters above, columns/actions below — instead of one ragged wrap. */
  .panel-tools { flex: none; display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 0.5rem; padding: 0.75rem 0 0.5rem; border-bottom: 1px solid ${css.grey7}; min-width: 0; }
  .tools-row { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; min-width: 0; }
  /* Wrapping is line-broken on flex-BASIS, so the search only absorbs slack once a line already fits —
     it fills the row on a wide screen and drops to its own line on a narrow one. */
  .tools-row .pager { flex: none; }
  .tools-row .spacer { margin-left: auto; }
  .tools-row .divider { width: 1px; align-self: stretch; background: ${css.grey7}; margin: 0 0.2rem; }
  /* Controls share one compact height so the rows read as a toolbar, not a stack of form fields. */
  .panel-tools select { min-width: 7.5rem; max-width: 13rem; height: 2rem; padding: 0 0.4rem; font-size: 0.82rem; }
  .panel-tools .search { flex: 1 1 12rem; min-width: 9rem; }
  /* The wrapper flexes, but the inner <input> keeps its browser-default width without this. */
  .panel-tools .search > * { width: 100%; }
  .panel-tools .search input { width: 100%; box-sizing: border-box; height: 2rem; font-size: 0.82rem; }
  .panel-tools .btn { height: 2rem; padding: 0 0.7rem; font-size: 0.82rem; white-space: nowrap; }
  .tools-row > label { display: inline-flex; align-items: center; height: 2rem; font-size: 0.82rem; white-space: nowrap; }
  .tools-row > label input { top: 0; width: 1.1rem; height: 1.1rem; margin-right: 0.4rem; }
  /* The stock FileInput is a 121px dashed drop-zone; here it has to read as one more toolbar button.
     Export is a real <Button>, Import is a FileInput target — ONE rule set covers both so the pair
     cannot drift apart visually. */
  .panel-tools .task-doc-upload { display: inline-flex; }
  /* button.xlsx-btn, not .xlsx-btn: Button's own .secondary/.compact rules are two-class selectors,
     so the element qualifier is what wins the cascade without resorting to !important. */
  .panel-tools .task-doc-upload .target,
  .panel-tools button.xlsx-btn {
    border: none; background: ${css.grey7}; color: ${css.grey2}; border-radius: 0.5rem; height: 2rem;
    min-height: 2rem; padding: 0 0.7rem; display: inline-flex; align-items: center; gap: 0.3rem;
    font-size: 0.82rem; line-height: 1; cursor: pointer; white-space: nowrap; text-transform: none;
  }
  .panel-tools .task-doc-upload .target:hover,
  .panel-tools button.xlsx-btn:hover { background: ${css.grey6}; }
  .panel-tools .task-doc-upload .target > div { line-height: 1; }
  .panel-tools .task-doc-upload .target .icon,
  .panel-tools button.xlsx-btn .icon { margin: 0; }
  .panel-tools .task-doc-upload .target .icon svg,
  .panel-tools button.xlsx-btn .icon svg {
    width: 0.9rem; height: 0.9rem; min-width: 0.9rem;
    /* Button paints nested icons white for its primary fill; these sit on grey. */
    fill: ${css.grey2};
  }
  /* The grid is taken OUT OF FLOW (absolute fill) so its 10k-px table can't contribute to any
     ancestor's intrinsic width. The app shell's .main-content has min-width: auto, so an in-flow wide
     table drags the whole page — and the toolbar's pager — off-screen. Positioning contains it here
     instead of changing shared layout for every other page. */
  .panel-scroll { flex: 1; min-height: 0; min-width: 0; position: relative; overflow: hidden; }
  .panel-scroll > * { position: absolute; inset: 0; overflow: auto; }
  .pager { display: flex; align-items: center; gap: 0.6rem; font-size: 0.85rem; color: ${css.grey3}; }
  .pager .count { white-space: nowrap; }
  .hint { color: ${css.grey4}; font-size: calc(0.8rem * var(--bulk-font-scale, 1)); margin: 0.5rem 0; }
  .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font-size: calc(0.82rem * var(--bulk-font-scale, 1)); }
  table.long { width: 100%; border-collapse: collapse; font-size: calc(0.85rem * var(--bulk-font-scale, 1)); }
  table.long th, table.long td { text-align: left; padding: 0.35rem 0.5rem; border-bottom: 1px solid ${css.grey7}; }
  table.long th { position: sticky; top: 0; background: ${css.grey9}; font-size: calc(0.72rem * var(--bulk-font-scale, 1)); text-transform: uppercase; letter-spacing: 0.04em; color: ${css.grey2}; }
  table.long .num { text-align: right; }
  table.long .cell-value { cursor: text; display: inline-block; min-width: 4rem; border-radius: 0.2rem; padding: 0.1rem 0.25rem; }
  table.long .cell-value:hover { background: ${css.lightBlue}; }
  table.long .cell-value.empty { color: ${css.grey5}; }
  table.long tr.saved td { background: ${css.grey9}; }
  .long-scroll { overflow: auto; }
`;

const PickerStyled = styled(ModalStyled)`
  .modal-window { width: 44rem; max-width: 100%; }
  .picker-search { margin-bottom: 0.5rem; display: flex; align-items: center; gap: 0.75rem; }
  .create-list { margin: 0.5rem 0; padding: 0.5rem 0.75rem; background: ${css.grey9}; border-left: 3px solid ${css.primaryColor}; border-radius: 0.25rem; max-height: 14rem; overflow: auto; }
  .create-list .create-head { font-weight: 600; color: ${css.grey2}; margin-bottom: 0.25rem; }
  .create-list .create-row { padding-left: 0.5rem; }
  .create-list .muted { color: ${css.grey4}; }
  .picker-search .select-all { flex: none; display: inline-flex; align-items: center; gap: 0.4rem; white-space: nowrap; cursor: pointer; user-select: none; font-size: 0.85rem; }
  .picker-search .pick-input { flex: 1; min-width: 0; }
  .picker-search .pick-input input { width: 100%; box-sizing: border-box; }
  .groups { max-height: 26rem; overflow-y: auto; }
  .group { margin-bottom: 0.75rem; }
  .group-title { font-weight: 600; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.05em; color: ${css.grey3}; margin-bottom: 0.25rem; }
  .opts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.1rem 1rem; }
  .opt { display: flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; }
  .modal-buttons { display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1rem; }
  .modal-buttons .left { margin-right: auto; display: flex; gap: 0.5rem; }
`;

/** Every column the picker can offer, grouped the way the template groups them. */
const useColumnCatalog = (lifecycleKey: string) => useMemo(() => {
  const lifecycle = getProjectManagementLifecycle(lifecycleKey);
  const spine: ProjectManagementBulkColumn[] = [
    'name', 'externalId', 'status', 'statusNote', 'ownerId', 'epcId', 'address', 'utilityId', 'capacityKwDc', 'storageKwh', 'portfolioId',
    'onsiteContactName', 'onsiteEmail', 'onsitePhone', 'clientId', 'sgipId',
  ].map((key) => ({ kind: 'spine' as const, key }));
  const seeds = lifecycle?.parameterSeeds || [];
  const parameters: ProjectManagementBulkColumn[] = seeds.flatMap((s) => ([
    { kind: 'parameter' as const, key: s.name, field: 'estimatedValue' as const },
    { kind: 'parameter' as const, key: s.name, field: 'actualValue' as const },
  ]));
  const documents: ProjectManagementBulkColumn[] = (lifecycle?.docSlots || []).map((d) => ({ kind: 'document' as const, key: d.docType }));
  const docLabel = new Map((lifecycle?.docSlots || []).map((d) => [d.docType, d.label]));
  return { lifecycle, spine, parameters, documents, docLabel };
}, [lifecycleKey]);

/**
 * One-click column sets. Built from the lifecycle catalogue (never a hardcoded name list) so a template
 * change flows straight through. `blocking` also switches the row filter on — that pairing is the whole
 * point of it: the parameters holding task completion up, and only the projects that have them.
 */
const bulkPresets = (catalog: ReturnType<typeof useColumnCatalog>) => {
  const seeds = catalog.lifecycle?.parameterSeeds || [];
  const byTasks = (keys: string[], field: 'estimatedValue' | 'actualValue' = 'actualValue'): ProjectManagementBulkColumn[] =>
    seeds.filter((s) => s.taskKey && keys.includes(s.taskKey)).map((s) => ({ kind: 'parameter' as const, key: s.name, field }));
  const spine = (keys: string[]): ProjectManagementBulkColumn[] => keys.map((key) => ({ kind: 'spine' as const, key }));
  return [
    {
      key: 'blocking', label: strings.Blocking(), onlyMissingActuals: true,
      columns: [...spine(['externalId']), ...seeds.filter((s) => s.taskKey).map((s) => ({ kind: 'parameter' as const, key: s.name, field: 'actualValue' as const }))],
    },
    { key: 'spine', label: strings.ProjectDetails(), columns: spine(['externalId', 'ownerId', 'epcId', 'address', 'utilityId', 'capacityKwDc', 'storageKwh', 'portfolioId']) },
    {
      key: 'tracker', label: strings.TrackerOrder(),
      columns: [...spine(['externalId', 'ownerId', 'utilityId']), ...seeds.slice(0, 8).flatMap((s) => ([
        { kind: 'parameter' as const, key: s.name, field: 'estimatedValue' as const },
        { kind: 'parameter' as const, key: s.name, field: 'actualValue' as const },
      ]))],
    },
    { key: 'modeling', label: strings.Modeling(), columns: [...spine(['externalId']), ...byTasks(['consumptionData', 'helioscope', 'etb', 'finalHelioscope'])] },
    { key: 'interconnection', label: strings.Interconnection(), columns: [...spine(['externalId']), ...byTasks(['ixApplication', 'design30', 'ixAgreement', 'ifpPlanSet', 'ifcPlanSet', 'permitIssued'])] },
    { key: 'construction', label: strings.Construction(), columns: [...spine(['externalId']), ...byTasks(['fntp', 'mechanicalElectrical', 'inspection', 'completion'])] },
  ];
};

/** A preset the menu can apply: either a template-derived built-in or an org-saved row. */
interface BulkPresetEntry {
  key: string;
  label: string;
  columns: ProjectManagementBulkColumn[];
  onlyMissingActuals?: boolean;
  saved?: ProjectManagementBulkPreset;   // present => editable/deletable by its author
}

const PresetMenuStyled = styled.div`
  padding: 0.35rem;
  min-width: 15rem;
  .row { display: flex; align-items: center; gap: 0.3rem; border-radius: 0.3rem; padding: 0.25rem 0.35rem; }
  .row:hover { background: ${css.grey9}; }
  .row .apply { flex: 1; text-align: left; background: none; border: none; cursor: pointer; font-size: 0.85rem; padding: 0; color: ${css.grey1}; }
  .row .who { color: ${css.grey5}; font-size: 0.7rem; white-space: nowrap; }
  .row .act { background: none; border: none; cursor: pointer; color: ${css.grey4}; padding: 0 0.1rem; font-size: 0.78rem; }
  .row .act:hover { color: ${css.primaryColor}; }
  .sep { height: 1px; background: ${css.grey7}; margin: 0.3rem 0; }
  .save-row { display: flex; gap: 0.3rem; align-items: center; }
  .save-row input { flex: 1; height: 1.8rem; font-size: 0.8rem; box-sizing: border-box; }
  .hint { color: ${css.grey4}; font-size: 0.72rem; padding: 0.2rem 0.35rem; }
`;

/**
 * Preset picker: applies a built-in or a saved column set, and manages the saved ones in place.
 * Built-ins are derived from the lifecycle template every render, so they follow template changes;
 * saved presets are a stored snapshot and are pruned against the live catalogue before use.
 */
const PresetMenu = (props: {
  entries: BulkPresetEntry[];
  canEdit: (p: ProjectManagementBulkPreset) => boolean;
  onApply: (e: BulkPresetEntry) => void;
  onSave: (name: string, preset?: ProjectManagementBulkPreset) => void;
  onDelete: (p: ProjectManagementBulkPreset) => void;
  onClose: () => void;
}) => {
  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState<ProjectManagementBulkPreset>();
  const builtIns = props.entries.filter((e) => !e.saved);
  const saved = props.entries.filter((e) => e.saved);
  return (
    <PresetMenuStyled>
      {
        builtIns.map((e) => (
          <div className="row" key={e.key}>
            <button className="apply" onClick={() => { props.onApply(e); props.onClose(); }}>{e.label}</button>
            <span className="who">{strings.BuiltIn()}</span>
          </div>
        ))
      }
      <div className="sep" />
      {
        saved.map((e) => (
          <div className="row" key={e.key}>
            {
              renaming && renaming._id === e.saved!._id ?
                <Input
                  value={name} autoFocus placeholder={strings.PresetName()}
                  onChange={(v) => setName(v || '')}
                  onEnter={() => { props.onSave(name, renaming); setRenaming(undefined); setName(''); }}
                />
                :
                <>
                  <button className="apply" onClick={() => { props.onApply(e); props.onClose(); }}>{e.label}</button>
                  <span className="who">{e.saved!.createdByName || ''}</span>
                  {
                    props.canEdit(e.saved!) &&
                    <>
                      <button className="act" title={strings.RenamePreset()} onClick={() => { setRenaming(e.saved); setName(e.label); }}>✎</button>
                      <button className="act" title={strings.Delete()} onClick={() => props.onDelete(e.saved!)}>✕</button>
                    </>
                  }
                </>
            }
          </div>
        ))
      }
      {!saved.length && <div className="hint">—</div>}
      <div className="sep" />
      <div className="save-row">
        <Input
          value={renaming ? '' : name} placeholder={strings.SavePresetAs()}
          onChange={(v) => { setRenaming(undefined); setName(v || ''); }}
          onEnter={() => { if (name.trim()) { props.onSave(name); setName(''); props.onClose(); } }}
        />
        <Button className="secondary compact" disabled={!name.trim() || !!renaming} onClick={() => { props.onSave(name); setName(''); props.onClose(); }}>
          {strings.Save()}
        </Button>
      </div>
    </PresetMenuStyled>
  );
};

/** Shows what a re-uploaded sheet would change BEFORE anything is written. */
const ImportPreviewModal = (props: { result: ProjectManagementBulkImportResult; busy: boolean; onApply: (createRows: number[]) => void; onClose: () => void }) => {
  const creates = props.result.creates || [];
  const blocked = !!props.result.createBlocked;
  // Every create starts ticked — accepting the sheet as it stands is the common case — and unticking one
  // drops that row entirely. A create is ~150 documents, not a cell, which is why it is reviewed by name
  // rather than counted. Blocked creates are still listed, so the sheet's intent is visible to someone
  // who cannot act on it, but they are not selectable.
  const [picked, setPicked] = useState<Set<number>>(() => new Set(creates.map((c) => c.row)));
  const toggle = (row: number) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(row)) next.delete(row);
    else next.add(row);
    return next;
  });
  const allPicked = creates.length > 0 && creates.every((c) => picked.has(c.row));
  const willCreate = blocked ? 0 : picked.size;
  return (
    <Modal styled={PickerStyled} title={strings.ReviewChanges()} onClose={props.onClose}>
      <div className="meta-row">{props.result.rows} {strings.Projects()} · {props.result.cells} {strings.CellsChanged()} · {props.result.projects} {strings.Projects()}</div>
      {!!creates.length &&
        <div className="create-list">
          <div className="meta-row create-head">
            {
              blocked
                ? `${strings.ProjectsToCreate(creates.length)} — ${strings.NotPermitted()}`
                : (
                  <Checkbox
                    checked={allPicked}
                    onChange={() => setPicked(allPicked ? new Set() : new Set(creates.map((c) => c.row)))}
                  >
                    {strings.ProjectsToCreate(picked.size)}
                  </Checkbox>
                )
            }
          </div>
          {creates.map((c) => (
            <div className="meta-row create-row" key={c.row}>
              {
                blocked
                  ? <span className="muted">{strings.Row()} {c.row}: {c.name} ({c.lifecycleLabel})</span>
                  : (
                    <Checkbox checked={picked.has(c.row)} onChange={() => toggle(c.row)}>
                      {strings.Row()} {c.row}: {c.name} ({c.lifecycleLabel})
                    </Checkbox>
                  )
              }
            </div>
          ))}
        </div>}
      {!!props.result.unknownColumns.length && <div className="meta-row">{strings.UnknownColumnsIgnored()}: {props.result.unknownColumns.join(', ')}</div>}
      {props.result.errors.slice(0, 5).map((e, i) => <div className="meta-row" key={i}>{e.key || e.projectId}: {e.message}</div>)}
      <div className="modal-buttons">
        <Button className="secondary" onClick={props.onClose}>{strings.Cancel()}</Button>
        <Button className="primary" disabled={props.busy || (!props.result.cells && !willCreate)} onClick={() => props.onApply([...picked])}>{strings.ApplyChanges()}</Button>
      </div>
    </Modal>
  );
};

/** Searchable column picker. Parameters keep the template's order rather than an alphabetical soup. */
const ColumnPicker = (props: {
  catalog: ReturnType<typeof useColumnCatalog>;
  selected: ProjectManagementBulkColumn[];
  label: (c: ProjectManagementBulkColumn) => string;
  onApply: (cols: ProjectManagementBulkColumn[]) => void;
  onClose: () => void;
}) => {
  const { catalog, label, onApply, onClose } = props;
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string[]>(props.selected.map(projectManagementBulkColId));
  const q = search.trim().toLowerCase();
  const toggle = (c: ProjectManagementBulkColumn) => {
    const id = projectManagementBulkColId(c);
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };
  const groups: { title: string, cols: ProjectManagementBulkColumn[] }[] = [
    { title: strings.ProjectDetails(), cols: catalog.spine },
    { title: strings.Details(), cols: catalog.parameters },
    { title: strings.Documents(), cols: catalog.documents },
  ];
  const all = [...catalog.spine, ...catalog.parameters, ...catalog.documents];
  // The master checkbox acts on the columns the search currently shows (so it doubles as "select all
  // matches"); with no search that is every column, i.e. a plain select-all / clear-all.
  const visibleIds = all.filter((c) => !q || label(c).toLowerCase().includes(q)).map(projectManagementBulkColId);
  const allVisiblePicked = visibleIds.length > 0 && visibleIds.every((id) => picked.includes(id));
  const toggleAll = () => setPicked((p) => (allVisiblePicked
    ? p.filter((id) => !visibleIds.includes(id))
    : [...new Set([...p, ...visibleIds])]));
  const apply = () => {
    // Preserve catalogue order so the grid always reads like the template.
    onApply(all.filter((c) => picked.includes(projectManagementBulkColId(c))));
    onClose();
  };
  return (
    <Modal styled={PickerStyled} title={strings.Columns()} onClose={onClose}>
      <div className="picker-search">
        <label className="select-all">
          <Checkbox checked={allVisiblePicked} onChange={toggleAll} />
          {allVisiblePicked ? strings.ClearColumns() : strings.SelectAll()}
        </label>
        <span className="pick-input"><Input value={search} onChange={(v) => setSearch(v || '')} placeholder={strings.Search()} /></span>
      </div>
      <div className="groups">
        {
          groups.map((g) => {
            const cols = g.cols.filter((c) => !q || label(c).toLowerCase().includes(q));
            if (!cols.length) return null;
            return (
              <div className="group" key={g.title}>
                <div className="group-title">{g.title}</div>
                <div className="opts">
                  {
                    cols.map((c) => {
                      const id = projectManagementBulkColId(c);
                      return (
                        <label className="opt" key={id}>
                          <Checkbox checked={picked.includes(id)} onChange={() => toggle(c)} />
                          {label(c)}
                        </label>
                      );
                    })
                  }
                </div>
              </div>
            );
          })
        }
      </div>
      <div className="modal-buttons">
        <span className="left">
          <Button className="secondary compact" onClick={() => setPicked([])}>{strings.ClearColumns()}</Button>
        </span>
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" onClick={apply}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

export const ProjectManagementBulkEditPage = () => {
  const navigate = useNavigate();
  const { mode: modeParam } = useParams<any>();
  const mode = modeParam === 'parameters' ? 'parameters' : 'projects';
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  // The vertical the workspace is on, shared with the pipeline — arriving here from a residential
  // pipeline must not silently hand back RIC's book.
  const [lifecycleKey, setLifecycleKey] = useState(projectManagementWorkspaceLifecycle);
  const catalog = useColumnCatalog(lifecycleKey);

  // Every spine column reads as it does on the project's Edit form — the same strings, so a person moving
  // between the two never meets the field under two names. The external id takes the vertical's own label
  // (RIC #, and so on), as it does everywhere else. A key with no entry would fall back to its raw name,
  // which is the thing this map exists to prevent; keep it complete when a spine column is added.
  const spineLabel: { [k: string]: () => string } = {
    name: strings.Name,
    externalId: () => projectManagementExternalIdLabel(lifecycleKey),
    status: strings.ProjectStatus,
    statusNote: strings.StatusReason,
    ownerId: strings.Owner,
    epcId: strings.Epc,
    address: strings.Address,
    utilityId: strings.UtilityCompany,
    capacityKwDc: strings.CapacityKwDc,
    storageKwh: strings.StorageKwh,
    portfolioId: strings.Portfolio,
    onsiteContactName: strings.OnsiteContactName,
    onsiteEmail: strings.OnsiteEmail,
    onsitePhone: strings.OnsitePhone,
    clientId: strings.ClientId,
    sgipId: strings.SgipId,
  };
  const columnLabel = (c: ProjectManagementBulkColumn): string => {
    if (c.kind === 'spine') return spineLabel[c.key]?.() || c.key;
    if (c.kind === 'document') return `${catalog.docLabel.get(c.key) || c.key} (${strings.Documents()})`;
    return `${c.key} · ${c.field === 'estimatedValue' ? strings.Estimated() : strings.Actual()}`;
  };

  const [columns, setColumns] = useState<ProjectManagementBulkColumn[]>(() => readColumns(lifecycleKey));
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  // The search box fires a portfolio-wide read per keystroke; only the debounced value drives fetches.
  const [appliedSearch, setAppliedSearch] = useState('');
  const [portfolioId, setPortfolioId] = useState('');
  const [onlyMissingActuals, setOnlyMissingActuals] = useState(false);
  const [names, setNames] = useState<string[]>([]);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [universe, setUniverse] = useState(0);
  const [filterState, setFilterState] = useState<BulkFilterState>(() => readFilterState());
  const [openFilter, setOpenFilter] = useState<{ column: ProjectManagementBulkColumn, trigger: HTMLElement }>();
  const [presetTrigger, setPresetTrigger] = useState<HTMLElement>();
  const [fontScale, setFontScale] = useState<number>(readFontScale);
  const [colWidths, setColWidths] = useState<Record<string, number>>(readColWidths);
  const [pinned, setPinned] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<ProjectManagementBulkRow[]>([]);
  const [paramRows, setParamRows] = useState<ProjectManagementBulkParameterRow[]>([]);
  const [paramNames, setParamNames] = useState<string[]>([]);
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [conflicts, setConflicts] = useState<Set<string>>(new Set());
  const [undoBatch, setUndoBatch] = useState<ProjectManagementBulkEdit[]>([]);
  const [editingLong, setEditingLong] = useState<{ id: string, field: 'estimatedValue' | 'actualValue', draft: any }>();
  const [fillColId, setFillColId] = useState('');
  const [importPreview, setImportPreview] = useState<{ result: ProjectManagementBulkImportResult, file: any }>();
  const [importBusy, setImportBusy] = useState(false);

  // Re-measured on any scroll or resize, so the panel tracks its heading across both grid axes.
  const filterAnchor = useAnchorRect(openFilter?.trigger);
  const presetAnchor = useAnchorRect(presetTrigger);
  const savedPresets = useTracker(() => ProjectManagementBulkPresets.find(
    { lifecycleKey, active: { $ne: false } }, { sort: { name: 1 } },
  ).fetch(), [lifecycleKey]);

  useSubscribe(Publications.projectManagementPipelineProjects, lifecycleKey);
  const portfolios = useTracker(() => ProjectManagementPortfolios.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const owners = useTracker(() => ProjectManagementOwners.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const epcs = useTracker(() => ProjectManagementEpcs.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const utilities = useTracker(() => ProjectManagementUtilities.find({ active: { $ne: false } }, { sort: { name: 1 } }).fetch(), []);
  const isAdmin = projectManagementIsAdmin(user, account);
  // Only the verticals this user has projects in, plus the one selected so the control can render its
  // own value — the pipeline's selector reads the same way.
  const [lifecyclesInUse] = useMethod(projectManagementLifecyclesInUse, undefined, [lifecycleKey]);
  const lifecycleOptions = Object.values(projectManagementLifecycles)
    .filter((l) => l.key === lifecycleKey || (lifecyclesInUse || []).includes(l.key))
    .map((l) => ({ value: l.key, label: l.label }));
  /**
   * Switch the book. Columns, filters, pins and paging all describe the vertical being left, so they are
   * replaced in the SAME handler as the key: letting an effect do it would write the outgoing vertical's
   * columns under the incoming one's storage key on the commit between.
   */
  const selectLifecycle = (key: string) => {
    if (key === lifecycleKey) return;
    setLifecycleKey(key);
    projectManagementSetWorkspaceLifecycle(key);
    setColumns(readColumns(key));
    setFilterState(emptyFilterState());
    setPinned(new Set());
    setNames([]);
    setSkip(0);
  };

  const filter = { lifecycleKey, search: appliedSearch.trim() || undefined, portfolioId: portfolioId || undefined, onlyMissingActuals };
  // Column filters and sort go ONLY to the grid and the export. The shared `filter` literal also feeds
  // projectManagementBulkParameters, which would drop them silently and change its count unexplained.
  const gridArg = { ...filter, columnFilters: filterState.filters, sort: filterState.sort };
  const filterSignature = JSON.stringify([filterState.filters, filterState.sort]);
  // load() has no in-flight cancellation; without a sequence guard a slow early response overwrites a
  // fast later one, leaving the grid showing a filter the user already changed.
  const loadSeq = useRef(0);
  const load = toastOnError(async (opts: { pinnedIds?: string[] } = {}) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      if (mode === 'parameters') {
        const res = await projectManagementBulkParameters.call({ ...filter, names: names.length ? names : undefined, skip, limit: PAGE_SIZE });
        if (seq !== loadSeq.current) return;
        setParamRows(res.rows);
        setParamNames(res.names);
        setTotal(res.total);
        setUniverse(res.total);
      }
      else {
        const res = await projectManagementBulkGrid.call({ ...gridArg, columns, skip, limit: PAGE_SIZE, pinnedIds: opts.pinnedIds });
        if (seq !== loadSeq.current) return;
        setRows(res.rows);
        setTotal(res.total);
        setUniverse(res.universe);
      }
    }
    finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  });
  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);
  // Snapshot fetch: filters/paging/mode/columns drive it, nothing else. (No subscription by design.)
  useEffect(() => { load({ pinnedIds: [...pinned] }); }, [mode, lifecycleKey, skip, appliedSearch, portfolioId, onlyMissingActuals, JSON.stringify(columns), JSON.stringify(names), filterSignature]);
  useEffect(() => { setSkip(0); }, [mode, appliedSearch, portfolioId, onlyMissingActuals, filterSignature]);
  useEffect(() => {
    try { localStorage.setItem(columnsKey(lifecycleKey), JSON.stringify(columns)); }
    catch (_) { /* private mode */ }
  }, [columns, lifecycleKey]);
  useEffect(() => {
    try { localStorage.setItem(FILTERS_KEY, JSON.stringify(filterState)); }
    catch (_) { /* private mode */ }
  }, [filterSignature]);
  useEffect(() => {
    try { localStorage.setItem(FONT_SCALE_KEY, String(fontScale)); }
    catch (_) { /* private mode */ }
  }, [fontScale]);
  useEffect(() => {
    try { localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(colWidths)); }
    catch (_) { /* private mode */ }
  }, [colWidths]);
  // A filter on a column that is no longer shown is invisible and unclearable. Pruning lives on
  // `columns` rather than in ColumnPicker.onApply because the preset Select sets columns directly.
  useEffect(() => {
    const shown = new Set(columns.map(projectManagementBulkColId));
    const keep = filterState.filters.filter((f) => shown.has(projectManagementBulkColId(f.column)));
    const sortGone = filterState.sort && !shown.has(projectManagementBulkColId(filterState.sort.column));
    if (keep.length !== filterState.filters.length || sortGone) {
      setFilterState({ v: 1, filters: keep, sort: sortGone ? undefined : filterState.sort });
    }
  }, [JSON.stringify(columns)]);
  // Paging is relative to a total the last write may have shrunk; clamp or the page renders empty.
  useEffect(() => {
    if (skip > 0 && skip >= total) setSkip(Math.max(0, Math.floor(Math.max(0, total - 1) / PAGE_SIZE) * PAGE_SIZE));
  }, [total]);

  /** Send a batch through bulkApply, chunked to the server cap, then repaint from the server. */
  const applyEdits = toastOnError(async (edits: ProjectManagementBulkEdit[], inverse: ProjectManagementBulkEdit[]) => {
    if (!edits.length) return;
    let applied = 0;
    const conflicted = new Set<string>();
    const errors: string[] = [];
    for (let i = 0; i < edits.length; i += projectManagementBulkApplyMax) {
      const res = await projectManagementBulkApply.call({ edits: edits.slice(i, i + projectManagementBulkApplyMax) });
      applied += res.applied;
      res.conflicts.forEach((c) => conflicted.add(`${c.projectId}:${c.key}`));
      res.errors.forEach((e) => errors.push(`${e.key}: ${e.message}`));
    }
    setConflicts(conflicted);
    setSaved(new Set(edits.map((e) => `${e.projectId}:${e.kind}:${e.key}:${e.field || ''}`)));
    setUndoBatch(inverse);
    if (errors.length) toast.error(errors.slice(0, 3).join(' · '));
    else if (conflicted.size) toast.warning(`${applied} ${strings.CellsChanged()} · ${conflicted.size} ${strings.EditedElsewhere()}`);
    else toast.success(`${applied} ${strings.CellsChanged()}`);
    // Filling a column that is itself filtered to blanks would otherwise erase its own results: every
    // row just written stops matching and vanishes mid-pass, taking the saved highlight and the undo
    // target with it. Pin what we touched — Refresh clears the pins and re-applies the filter honestly.
    const touched = new Set([...pinned, ...edits.map((e) => e.projectId)]);
    setPinned(touched);
    await load({ pinnedIds: [...touched] });
  });

  /** One cell edit in Projects mode. */
  const commitCell = (row: ProjectManagementBulkRow, column: ProjectManagementBulkColumn, value: any) => {
    const cell = row.cells[projectManagementBulkColId(column)] || {};
    const edit: ProjectManagementBulkEdit = {
      projectId: row.projectId, kind: column.kind === 'parameter' ? 'parameter' : 'spine', key: column.key,
      field: column.field, value, expectedUpdatedAt: cell.updatedAt,
    };
    const inverse: ProjectManagementBulkEdit = { ...edit, value: (cell.value as any) ?? null, expectedUpdatedAt: undefined };
    applyEdits([edit], [inverse]);
  };

  /** TSV paste starting at a cell: fills down/right across the visible columns and page rows. */
  const pasteFrom = (row: ProjectManagementBulkRow, column: ProjectManagementBulkColumn, text: string) => {
    const grid = text.replace(/\r/g, '').split('\n').filter((l, i, a) => l.length || i < a.length - 1).map((l) => l.split('\t'));
    const startRow = rows.findIndex((r) => r.projectId === row.projectId);
    const startCol = columns.findIndex((c) => projectManagementBulkColId(c) === projectManagementBulkColId(column));
    const edits: ProjectManagementBulkEdit[] = [];
    const inverse: ProjectManagementBulkEdit[] = [];
    grid.forEach((line, dy) => line.forEach((text2, dx) => {
      const targetRow = rows[startRow + dy];
      const targetCol = columns[startCol + dx];
      if (!targetRow || !targetCol || targetCol.kind === 'document') return;
      const cell = targetRow.cells[projectManagementBulkColId(targetCol)] || {};
      const type = targetCol.kind === 'parameter' ? cell.type : projectManagementSpineFieldType(targetCol.key);
      const value = projectManagementParseCell(type as any, text2);
      edits.push({
        projectId: targetRow.projectId, kind: targetCol.kind === 'parameter' ? 'parameter' : 'spine',
        key: targetCol.key, field: targetCol.field, value, expectedUpdatedAt: cell.updatedAt,
      });
      inverse.push({
        projectId: targetRow.projectId, kind: targetCol.kind === 'parameter' ? 'parameter' : 'spine',
        key: targetCol.key, field: targetCol.field, value: (cell.value as any) ?? null,
      });
    }));
    if (!edits.length) return;
    // eslint-disable-next-line no-alert
    if (!confirm(`${edits.length} ${strings.CellsChanged()}?`)) return;
    applyEdits(edits, inverse);
  };

  /** Fill the SELECTED column down the current page (page-scoped on purpose — see the plan's §11.3). */
  const fillColumn = () => {
    const col = columns.find((c) => c.kind !== 'document' && projectManagementBulkColId(c) === fillColId);
    if (!col || !rows.length) return;
    const first = rows[0].cells[projectManagementBulkColId(col)];
    // eslint-disable-next-line no-alert
    const raw = prompt(`${columnLabel(col)} — ${strings.ApplyToPage()} (${rows.length})`, first?.value == null ? '' : String(first.value));
    if (raw === null) return;
    const edits: ProjectManagementBulkEdit[] = [];
    const inverse: ProjectManagementBulkEdit[] = [];
    for (const row of rows) {
      const cell = row.cells[projectManagementBulkColId(col)] || {};
      const type = col.kind === 'parameter' ? cell.type : projectManagementSpineFieldType(col.key);
      edits.push({ projectId: row.projectId, kind: col.kind === 'parameter' ? 'parameter' : 'spine', key: col.key, field: col.field, value: projectManagementParseCell(type as any, raw), expectedUpdatedAt: cell.updatedAt });
      inverse.push({ projectId: row.projectId, kind: col.kind === 'parameter' ? 'parameter' : 'spine', key: col.key, field: col.field, value: (cell.value as any) ?? null });
    }
    applyEdits(edits, inverse);
  };

  const exportXlsx = toastOnError(async () => {
    const url = await projectManagementBulkExport.call({ ...filter, columns });
    window.open(url, '_blank');
  });
  /** Import runs as a DRY RUN first: the user sees the diff, then applies it through the same path.
   *  `file` is already a FileMeta — FileInput uploads on selection. */
  const previewImport = toastOnError(async (file: any) => {
    setImportBusy(true);
    try {
      const result = await projectManagementBulkImport.call({ file, dryRun: true, lifecycleKey });
      // Said once, on the preview: a row with no Project ID is a request to create, and this caller can't.
      if (result.createBlocked) toast.warn(strings.CannotCreateProjectsHere());
      setImportPreview({ result, file });
    }
    finally {
      setImportBusy(false);
    }
  });
  const applyImport = toastOnError(async (createRows: number[]) => {
    if (!importPreview) return;
    setImportBusy(true);
    try {
      const result = await projectManagementBulkImport.call({ file: importPreview.file, dryRun: false, lifecycleKey, createRows });
      toast.success([
        result.created ? strings.ProjectsCreated(result.created) : '',
        `${result.applied || 0} ${strings.CellsChanged()}`,
      ].filter(Boolean).join(' · '));
      setImportPreview(undefined);
      await load();
    }
    finally {
      setImportBusy(false);
    }
  });

  const uploadDocument = toastOnError(async (row: ProjectManagementBulkRow, docType: string, file: any) => {
    await projectManagementBatchUploadDocument.call({ projectManagementProjectId: row.projectId, docType, file });
    toast.success(strings.Done());
    await load();
  });

  /** One cell edit in Parameters (long) mode. */
  const commitLong = (r: ProjectManagementBulkParameterRow, field: 'estimatedValue' | 'actualValue', raw: any) => {
    setEditingLong(undefined);
    const stored = projectManagementToStored(r.type, raw);
    if ((stored ?? null) === ((r[field] as any) ?? null)) return;
    applyEdits(
      [{ projectId: r.projectId, kind: 'parameter', key: r.name, field, value: stored ?? null, expectedUpdatedAt: r.updatedAt }],
      [{ projectId: r.projectId, kind: 'parameter', key: r.name, field, value: (r[field] as any) ?? null }],
    );
  };

  if (!projectManagementCanAccess(user, account)) return <NotPermittedPage />;
  // Portfolio-wide editing is an admin surface, never a stakeholder one.
  if (!isAdmin) return <NotPermittedPage />;

  const presets = bulkPresets(catalog);
  // Built-ins are re-derived from the template each render; saved presets are a stored snapshot, so
  // their columns are pruned against the live catalogue before use (a retired parameter would
  // otherwise render as a dead column that always reads blank).
  const validColIds = new Set([...catalog.spine, ...catalog.parameters, ...catalog.documents].map(projectManagementBulkColId));
  const presetEntries: BulkPresetEntry[] = [
    ...presets.map((p) => ({ key: p.key, label: p.label, columns: p.columns, onlyMissingActuals: (p as any).onlyMissingActuals })),
    ...savedPresets.map((p) => ({
      key: p._id, label: p.name, columns: projectManagementBulkPresetPrune(p.columns, validColIds), saved: p,
    })),
  ];
  const canEditPreset = (p: ProjectManagementBulkPreset) => !!user?.isSuperUser || p.createdByUserId === user?._id;
  /** Apply a preset: swap the column set, and mirror the built-in Blocking pairing where it has one. */
  const applyPreset = (e: BulkPresetEntry) => {
    const dropped = (e.saved?.columns.length || 0) - e.columns.length;
    setColumns(e.columns);
    if (e.onlyMissingActuals !== undefined) setOnlyMissingActuals(!!e.onlyMissingActuals);
    setFillColId('');
    if (dropped > 0) toast.info(`${dropped} ${strings.PresetColumnsDropped()}`);
  };
  const savePreset = toastOnError(async (name: string, existing?: ProjectManagementBulkPreset) => {
    await projectManagementSaveBulkPreset.call({
      _id: existing?._id, name, lifecycleKey,
      // A rename keeps the stored columns; a fresh save snapshots what is on screen now.
      columns: existing ? existing.columns : columns,
    });
    toast.success(strings.Saved());
  });
  const deletePreset = toastOnError(async (p: ProjectManagementBulkPreset) => {
    // eslint-disable-next-line no-alert
    if (!confirm(strings.DeletePresetConfirm())) return;
    await projectManagementDeleteBulkPreset.call({ _id: p._id });
    toast.success(strings.Deleted());
  });
  // Still computed for the hidden fill-column control; void keeps the build quiet while it's shelved.
  const fillableColumns = columns.filter((c) => c.kind !== 'document');
  void fillableColumns; void fillColumn;
  const pageEnd = Math.min(skip + PAGE_SIZE, total);
  const filteredColIds = new Set(filterState.filters.map((f) => projectManagementBulkColId(f.column)));
  const headerSort = filterState.sort
    ? { colId: projectManagementBulkColId(filterState.sort.column), dir: filterState.sort.dir }
    : undefined;

  /** A column's filter type: the template seed first — it is the only source when the project has no
   *  parameter row at all, which is common — then the type stored on an existing row. */
  const columnFilterType = (c: ProjectManagementBulkColumn): ProjectManagementParameterType | 'document' => {
    if (c.kind === 'document') return 'document';
    if (c.kind === 'spine') return projectManagementSpineFieldType(c.key) === 'number' ? 'number' : 'string';
    const seed = (catalog.lifecycle?.parameterSeeds || []).find((s) => s.name === c.key);
    if (seed?.type) return seed.type;
    const cell = rows.find((r) => r.cells[projectManagementBulkColId(c)]?.type)?.cells[projectManagementBulkColId(c)];
    return cell?.type || 'number';
  };
  const columnUnit = (c: ProjectManagementBulkColumn): string | undefined => {
    if (c.kind !== 'parameter') return undefined;
    const seed = (catalog.lifecycle?.parameterSeeds || []).find((s) => s.name === c.key);
    return seed?.unit || rows.find((r) => r.cells[projectManagementBulkColId(c)]?.unit)?.cells[projectManagementBulkColId(c)]?.unit;
  };

  /** Replaces (or drops) one column's filter; editing a filter always returns to page 1 via the effect. */
  const setColumnFilter = (column: ProjectManagementBulkColumn, next?: ProjectManagementBulkColumnFilter) => {
    const colId = projectManagementBulkColId(column);
    const rest = filterState.filters.filter((f) => projectManagementBulkColId(f.column) !== colId);
    setPinned(new Set());
    setFilterState({ ...filterState, filters: next ? [...rest, next] : rest });
  };
  const setColumnSort = (column: ProjectManagementBulkColumn, dir?: 1 | -1) => {
    setFilterState({ ...filterState, sort: dir ? { column, dir } : undefined });
  };
  /** Set (or clear, when width is undefined) one column's width. */
  const resizeColumn = (colId: string, width?: number) => setColWidths((prev) => {
    const next = { ...prev };
    if (width === undefined) delete next[colId];
    else next[colId] = Math.round(width);
    return next;
  });
  const clearAllFilters = () => { setPinned(new Set()); setFilterState(emptyFilterState()); };
  /** Refresh drops the pins, so the filter is re-applied honestly to what is now stored. */
  const refresh = () => { setPinned(new Set()); load(); };

  return (
    <BulkPageStyled>
      <PageHeading
        left={<h1>{strings.BulkEdit()}</h1>}
        right={
          <div className="heading-buttons">
            {!!undoBatch.length && <Button className="secondary" onClick={() => { const inv = undoBatch; setUndoBatch([]); applyEdits(inv, []); }}>{strings.UndoLastBatch()}</Button>}
            <Button className="secondary" onClick={refresh}>{strings.Refresh()}</Button>
            <Button className="secondary" onClick={() => navigate('/project-management')}>{strings.Back()}</Button>
          </div>
        }
      />
      <PageBody>
        <DividerTabs
          activeTab={mode}
          onTabChange={(t) => navigate(`/project-management/bulk/${t}`)}
          tabs={[{ id: 'projects', label: strings.Projects() }, { id: 'parameters', label: strings.Details() }]}
        />
        <div className="panel bulk-panel">
          <div className="panel-tools">
            {/* Row 1 — what you are looking at. */}
            <div className="tools-row primary">
              <span className="search"><Input value={search} onChange={(v) => setSearch(v || '')} placeholder={strings.Search()} /></span>
              <Select value={lifecycleKey} options={lifecycleOptions} onChange={(v) => selectLifecycle(v)} />
              <Select value={portfolioId} options={[{ value: '', label: strings.AllPortfolios() }, ...portfolios.map((p) => ({ value: p._id, label: p.name }))]} onChange={(v) => setPortfolioId(v || '')} />
              {
                mode === 'parameters' &&
                <Select
                  value={names[0] || ''}
                  options={[{ value: '', label: strings.AllParameters() }, ...paramNames.map((n) => ({ value: n, label: n }))]}
                  onChange={(v) => setNames(v ? [v] : [])}
                />
              }
              <Checkbox checked={onlyMissingActuals} onChange={(v) => setOnlyMissingActuals(!!v)}>{strings.Blocking()}</Checkbox>
              <span className="spacer" />
              <span className="pager">
                <Button className="secondary compact" disabled={skip <= 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>‹</Button>
                <span className="count">
                  {total ? skip + 1 : 0}–{pageEnd} / {total}
                  {/* With a filter on, the unfiltered count is what makes the filter's effect legible. */}
                  {!!filterState.filters.length && universe !== total && ` (${strings.FilteredOfTotal()} ${universe})`}
                </span>
                <Button className="secondary compact" disabled={pageEnd >= total} onClick={() => setSkip(skip + PAGE_SIZE)}>›</Button>
              </span>
            </div>
            {/* Row 2 — what you can do to it (projects mode only; the long view has no columns to shape). */}
            {
              mode === 'projects' &&
              <div className="tools-row">
                <Button
                  className="secondary compact"
                  // Capture the node NOW: React nulls currentTarget once the handler returns, and a
                  // functional updater reads it later — the menu would then open anchored to nothing.
                  onClick={(e) => { const el = e.currentTarget as HTMLElement; setPresetTrigger((cur) => (cur ? undefined : el)); }}
                >
                  {strings.Preset()} ▾
                </Button>
                <Button className="secondary compact" onClick={() => setShowPicker(true)}>{strings.Columns()} ({columns.length})</Button>
                {
                  !!filterState.filters.length &&
                  <Button className="secondary compact" onClick={clearAllFilters}>
                    {strings.ClearFilters()} ({filterState.filters.length})
                  </Button>
                }
                {
                  !!Object.keys(colWidths).length &&
                  <Button className="secondary compact" onClick={() => setColWidths({})}>
                    {strings.ResetColumnWidths()} ({Object.keys(colWidths).length})
                  </Button>
                }
                {/* Fill-column hidden 2026-08-11 — fillColumn()/fillColId stay wired for when it returns.
                <span className="divider" />
                <Select
                  value={fillColId}
                  options={[{ value: '', label: strings.FillColumn() }, ...fillableColumns.map((c) => ({ value: projectManagementBulkColId(c), label: columnLabel(c) }))]}
                  onChange={(v) => setFillColId(v || '')}
                />
                <Button className="secondary compact" disabled={!fillColId} onClick={() => fillColumn()}>{strings.ApplyToPage()}</Button>
                */}
                <span className="spacer" />
                <Select
                  className="font-scale"
                  value={String(fontScale)}
                  options={FONT_SCALES.map((f) => ({ value: String(f), label: `${strings.TextSize()} ${Math.round(f * 100)}%` }))}
                  onChange={(v) => setFontScale(Number(v) || 1)}
                />
                <Button className="secondary compact xlsx-btn" onClick={() => exportXlsx()}>
                  <Icon icon="upload" />
                  {strings.ExportXlsx()}
                </Button>
                <FileInput className="task-doc-upload" label={strings.ImportXlsx()} maxSize={maxSpreadsheetUploadBytes} onChange={(f) => previewImport(f)} />
              </div>
            }
          </div>
          <div className="panel-scroll" style={{ '--bulk-font-scale': fontScale } as React.CSSProperties}>
            {
              // The grid stays MOUNTED across a refetch — swapping it for a spinner would tear the
              // <th> out from under an open filter popover, and flash the whole table on every change.
              loading && !rows.length ?
                <LoadingFlex />
                : mode === 'projects' ?
                  (
                    !columns.length ?
                      <div className="hint">{strings.NoColumnsSelected()}</div>
                      :
                      <ProjectManagementBulkGrid
                        columns={columns}
                        rows={rows}
                        columnLabel={columnLabel}
                        referenceOptions={{
                          // A fixed list, not a registry — the same four the status modal offers, labelled the same.
                          status: projectManagementProjectStatuses.map((s) => ({ value: s, label: projectStatusLabel(s) })),
                          portfolioId: portfolios.map((p) => ({ value: p._id, label: p.name })),
                          ownerId: owners.map((o: ProjectManagementParty) => ({ value: o._id, label: o.name })),
                          epcId: epcs.map((e: ProjectManagementParty) => ({ value: e._id, label: e.name })),
                          utilityId: utilities.map((u: ProjectManagementParty) => ({ value: u._id, label: u.name })),
                        }}
                        saved={saved}
                        conflicts={conflicts}
                        pinned={pinned}
                        filteredColIds={filteredColIds}
                        sort={headerSort}
                        onCommit={commitCell}
                        onUploadDocument={uploadDocument}
                        onPaste={pasteFrom}
                        onOpenProject={(id) => navigate(`/project-management/projects/${id}`)}
                        colWidths={colWidths}
                        onResizeColumn={resizeColumn}
                        onOpenFilter={(column, trigger) => setOpenFilter((cur) =>
                          (cur && projectManagementBulkColId(cur.column) === projectManagementBulkColId(column) ? undefined : { column, trigger }))}
                      />
                  )
                  :
                  <div className="long-scroll">
                    <table className="long">
                      <thead>
                        <tr>
                          <th>{strings.Name()}</th><th>{strings.Details()}</th><th>{strings.Task()}</th>
                          <th>{strings.Estimated()}</th><th>{strings.Actual()}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          paramRows.map((r) => (
                            <tr key={r.parameterId}>
                              <td><button className="link-btn" onClick={() => navigate(`/project-management/projects/${r.projectId}`)}>{r.projectName}</button></td>
                              <td>{r.name}{r.unit ? ` (${r.unit})` : ''}</td>
                              <td>{r.taskLabel || '—'}</td>
                              {
                                (['estimatedValue', 'actualValue'] as const).map((field) => (
                                  <td key={field}>
                                    {
                                      editingLong?.id === r.parameterId && editingLong.field === field ?
                                        projectManagementValueInput(r.type, editingLong.draft, (v) => setEditingLong({ id: r.parameterId, field, draft: v }), {
                                          onBlur: () => commitLong(r, field, editingLong.draft),
                                        })
                                        :
                                        <span
                                          className={`cell-value${r[field] == null || r[field] === '' ? ' empty' : ''}`}
                                          onClick={() => setEditingLong({ id: r.parameterId, field, draft: r[field] })}
                                        >
                                          {formatValue(r[field], r.unit, r.type)}
                                        </span>
                                    }
                                  </td>
                                ))
                              }
                            </tr>
                          ))
                        }
                        {!paramRows.length && <tr><td className="hint" colSpan={5}>—</td></tr>}
                      </tbody>
                    </table>
                  </div>
            }
          </div>
        </div>
      </PageBody>
      {
        presetTrigger &&
        <Popover anchor={presetAnchor} trigger={presetTrigger} width={300} onClose={() => setPresetTrigger(undefined)}>
          <PresetMenu
            entries={presetEntries}
            canEdit={canEditPreset}
            onApply={applyPreset}
            onSave={(name, existing) => savePreset(name, existing)}
            onDelete={(pr) => deletePreset(pr)}
            onClose={() => setPresetTrigger(undefined)}
          />
        </Popover>
      }
      {
        openFilter &&
        <Popover anchor={filterAnchor} trigger={openFilter.trigger} width={280} onClose={() => setOpenFilter(undefined)}>
          <ProjectManagementColumnFilter
            column={openFilter.column}
            label={columnLabel(openFilter.column)}
            type={columnFilterType(openFilter.column)}
            unit={columnUnit(openFilter.column)}
            filter={filterState.filters.find((f) => projectManagementBulkColId(f.column) === projectManagementBulkColId(openFilter.column))}
            sortDir={headerSort?.colId === projectManagementBulkColId(openFilter.column) ? headerSort.dir : undefined}
            loadValues={() => projectManagementBulkColumnValues.call({ ...gridArg, columns, column: openFilter.column })}
            onApply={(next) => setColumnFilter(openFilter.column, next)}
            onSort={(dir) => setColumnSort(openFilter.column, dir)}
            onClose={() => setOpenFilter(undefined)}
          />
        </Popover>
      }
      {
        showPicker &&
        <ColumnPicker catalog={catalog} selected={columns} label={columnLabel} onApply={setColumns} onClose={() => setShowPicker(false)} />
      }
      {
        importPreview &&
        <ImportPreviewModal result={importPreview.result} busy={importBusy} onApply={(createRows) => applyImport(createRows)} onClose={() => setImportPreview(undefined)} />
      }
    </BulkPageStyled>
  );
};
